import { apiServer } from "../config";
import { fetchWithToken } from "./fetchWithToken";

export interface SaveSearchResponse {
  status: string;
  message: string;
  data: {
    search: {
      id: number;
      name: string;
      description: string;
      search_term: string;
      filters: {
        business_types: string[] | string;
        accessibilities: number[];
        opening_hours?: {
          day: number[];
          start: string;
          end: string;
        };
        open_now: boolean;
        // New parameters
        latitude?: string;
        longitude?: string;
        address?: string;
        search_type?: string;
        search_value?: string;
        tagging_mode?: string;
        placesToReplaceString?: string;
      };
      is_public: boolean;
      share_token: any;
      created_at: string;
    };
  };
}

export async function saveSearch(
  search: {
    name: string;
    description: string;
    searchTerm: string;
    types: string;
    accessibilities: string;
    showVenueOptions: string;
    openingHoursDay: string;
    openingHoursHour: string;
    openingHoursMinute: string;
    // New parameters
    latitude?: string;
    longitude?: string;
    address?: string;
    search_type?: string;
    search_value?: string;
    tagging_mode?: string;
    placesToReplaceString?: string;
  },
  token: string,
) {
  const formData = new FormData();
  formData.append("name", search.name);
  formData.append("description", search.description);
  formData.append("search_term", search.searchTerm);
  formData.append("types", search.types);
  formData.append("accessibilities", search.accessibilities);
  formData.append("showVenueOptions", search.showVenueOptions);
  formData.append("openingHoursDay", search.openingHoursDay);

  if (search.openingHoursHour !== "" || search.openingHoursMinute !== "") {
    // Ensure minute is zero-padded (e.g., "3" -> "03")
    const formattedMinute = search.openingHoursMinute
      .toString()
      .padStart(2, "0");
    formData.append(
      "openingHoursStartTime",
      `${search.openingHoursHour}:${formattedMinute}`,
    );
  } else {
    formData.append("openingHoursStartTime", "");
  }

  formData.append("openingHoursEndTime", "");

  // Add new parameters to FormData
  if (search.latitude) formData.append("latitude", search.latitude);
  if (search.longitude) formData.append("longitude", search.longitude);
  if (search.address) formData.append("address", search.address);
  if (search.search_type) formData.append("search_type", search.search_type);
  if (search.search_value) formData.append("search_value", search.search_value);
  if (search.tagging_mode) formData.append("tagging_mode", search.tagging_mode);
  if (search.placesToReplaceString)
    formData.append("placesToReplaceString", search.placesToReplaceString);

  console.log("formData", formData);
  const response = await fetchWithToken(`${apiServer}/user/searches/saved`, {
    method: "POST",
    body: formData,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response) {
    return null;
  }
  const data = (await response.json()) as SaveSearchResponse;
  console.log("searches/saved data", data);
  if (data.status === "error") {
    throw new Error(data.message);
  }
  return data.data;
}

export async function updateSavedSearch(
  search: {
    id: number;
    name: string;
    description: string;
    searchTerm: string;
    types: string;
    accessibilities: string;
    showVenueOptions: string;
    openingHoursDay: string;
    openingHoursHour: string;
    openingHoursMinute: string;
    openingHoursPeriod: string;
    // New parameters
    latitude?: string;
    longitude?: string;
    address?: string;
    search_type?: string;
    search_value?: string;
    tagging_mode?: string;
    placesToReplaceString?: string;
  },
  token: string,
) {
  const formData = new FormData();
  formData.append("id", search.id.toString());
  formData.append("name", search.name);
  formData.append("description", search.description);
  formData.append("search_term", search.searchTerm);
  formData.append("types", search.types);
  formData.append("accessibilities", search.accessibilities);
  formData.append("showVenueOptions", search.showVenueOptions);
  formData.append("openingHoursDay", search.openingHoursDay);

  if (search.openingHoursHour !== "" || search.openingHoursMinute !== "") {
    const formattedMinute = search.openingHoursMinute
      .toString()
      .padStart(2, "0");
    formData.append(
      "openingHoursStartTime",
      `${search.openingHoursHour}:${formattedMinute} ${search.openingHoursPeriod}`,
    );
  } else {
    formData.append("openingHoursStartTime", "");
  }

  formData.append("openingHoursEndTime", "");

  // Add new parameters to FormData
  if (search.latitude) formData.append("latitude", search.latitude);
  if (search.longitude) formData.append("longitude", search.longitude);
  if (search.address) formData.append("address", search.address);
  if (search.search_type) formData.append("search_type", search.search_type);
  if (search.search_value) formData.append("search_value", search.search_value);
  if (search.tagging_mode) formData.append("tagging_mode", search.tagging_mode);
  if (search.placesToReplaceString)
    formData.append("placesToReplaceString", search.placesToReplaceString);

  console.log("formData", formData);
  const response = await fetchWithToken(`${apiServer}/user/searches/update`, {
    method: "POST",
    body: formData,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response) {
    return null;
  }
  const data = (await response.json()) as SaveSearchResponse;
  if (data.status === "error") {
    throw new Error(data.message);
  }
  return data;
}

export async function deleteSavedSearch(id: number, token: string) {
  const formData = new FormData();
  formData.append("id", id.toString());
  const response = await fetchWithToken(`${apiServer}/user/searches/delete`, {
    method: "POST",
    body: formData,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (!response) {
    throw new Error("Failed to delete saved search");
  }
  const data = (await response.json()) as SaveSearchResponse;
  if (data.status === "error") {
    throw new Error(data.message);
  }
  return data.data;
}
